import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

// Add type definitions for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Extract this outside the component to avoid reinitialization
let consentInitialized = false;

export function CookieConsent() {
  const [show, setShow] = useState(false);
  
  // Use a separate dynamic import for the consent logic
  useEffect(() => {
    if (consentInitialized) return;
    consentInitialized = true;
    
    const initConsent = () => {
      // Check if user has already made a choice
      const consentStatus = localStorage.getItem('consentStatus');
      if (!consentStatus) {
        // Use a reasonable delay (3 seconds) - long enough for LCP but short enough to meet legal requirements
        setTimeout(() => {
          setShow(true);
        }, 3000);
      }
    };
    
    // Wait until everything else is loaded but ensure it happens
    if (document.readyState === 'complete') {
      initConsent();
    } else {
      window.addEventListener('load', initConsent);
      
      // Fallback to ensure it always shows, even if 'load' event doesn't fire
      setTimeout(initConsent, 4000);
      
      return () => window.removeEventListener('load', initConsent);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('consentStatus', 'granted');
    setShow(false);

    // Update consent in Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
  };

  const handleDecline = () => {
    localStorage.setItem('consentStatus', 'denied');
    setShow(false);
    
    // Update consent in Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
  };

  if (!show) return null;

  // Use Portal to render outside the main DOM tree
  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-black border-t border-neutral-800 p-4 z-50"
      aria-live="polite"
      data-cookie-consent="true"
      style={{
        opacity: 0.95, // Slightly transparent
        willChange: 'transform', // Optimize for animation
        transform: 'translateZ(0)' // Force GPU acceleration
      }}
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-grow">
          <h3 className="text-lg font-semibold mb-1">Cookie Consent</h3>
          <p className="text-neutral-400 text-sm">
            We use cookies to enhance your experience and analyze our website traffic.
            By clicking "Accept", you consent to our use of cookies. You can decline
            and still use our website with limited functionality.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDecline}
            className="px-4 py-2 bg-neutral-800 hover:bg-neutral-600 rounded-lg transition-colors"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
          >
            Accept
          </button>
          <button
            onClick={handleDecline}
            className="p-2 hover:bg-neutral-900 rounded-full transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}